import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NotificationService } from '@shared/services/notification.service';

interface VersionManifest {
  version: string;
}

// 10 min — no hace falta más agresivo que esto: el chequeo real que importa
// es al volver a la pestaña (ver onVisibilityChange), este intervalo es solo
// red de contención para una pestaña que queda en primer plano sin blur nunca.
const POLL_INTERVAL_MS = 10 * 60 * 1000;

/**
 * TRANS-09 — el auto-refresh de versión del legacy vivía en el backend porque
 * compartía repo/deploy con el frontend; ahora que smagfe-ui se despliega por
 * separado, el chequeo vive 100% acá: contra su propio `/version.json`
 * (generado en build time, ver scripts/write-version.js), servido con
 * Cache-Control: no-cache (nginx.conf.template) para que el polling nunca
 * vea una respuesta cacheada vieja.
 *
 * A diferencia del legacy (reload silencioso automático), esto solo avisa —
 * NotificationService.promptUpdate() deja que el usuario decida cuándo
 * actualizar, para no tirarle un formulario a medio llenar.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private http   = inject(HttpClient);
  private notify = inject(NotificationService);

  private baseVersion: string | null = null;
  private notified = false;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  start(): void {
    if (this.intervalId) return; // ya arrancado — no duplicar listeners/intervalos
    this.checkNow();
    this.intervalId = setInterval(() => this.checkNow(), POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') this.checkNow();
  };

  checkNow(): void {
    this.http.get<VersionManifest>('/version.json').subscribe({
      next: res => {
        if (this.baseVersion === null) {
          this.baseVersion = res.version;
          return;
        }
        if (!this.notified && res.version !== this.baseVersion) {
          this.notified = true;
          this.notify.promptUpdate(() => window.location.reload());
        }
      },
      // Silencioso a propósito: un fallo de red al chequear la versión no debe
      // interrumpir a nadie ni loguear ruido — simplemente se reintenta en el
      // próximo ciclo.
      error: () => {},
    });
  }
}
